import type React from 'react';

import { useTranslation } from 'next-i18next';

import useAlerts from 'src/hooks/useAlerts';
import useLoginModal from 'src/hooks/useLoginModal';

import { useFollowMutation, useUnFollowMutation } from 'src/hooks/__generated__/queries';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import type { Company, User } from 'src/types/__generated__/graphql';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const useFollowUnfollow = ({
    user,
    company,
    refetchQueries = [],
}: {
    user?: NonNullable<Pick<User, 'id' | 'firstName' | 'fullName' | 'isFollowed'>>;
    company?: Pick<Company, 'id' | 'name' | 'isFollowingCompany'>;
    refetchQueries?: string[];
}) => {
    const { addAlert } = useAlerts();
    const { createAuthenticatedHandler } = useLoginModal();
    const { followUser, unfollowUser, followCompany, unfollowCompany } = useApolloCacheUpdate();
    const { t } = useTranslation(['profile']);
    const { logEventDynamic } = useDynamicLogEvents();
    const combinedRefetchQueries = ['FollowedUsers', 'CurrentUserProfile', ...refetchQueries];

    const [followMutation, { loading: followLoading }] = useFollowMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            if (user) {
                followUser(user.id);
            }
            if (company) {
                followCompany(company.id);
            }
            addAlert({
                type: 'success',
                message: String(t('profile:followSuccess', { name: user?.firstName ?? company?.name })),
            });
        },
    });

    const [unfollowMutation, { loading: unfollowLoading }] = useUnFollowMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            if (user) {
                unfollowUser(user.id);
            }
            if (company) {
                unfollowCompany(company.id);
            }
        },
    });
    const mutationParams = {
        variables: {
            userId: user?.id as string,
            companyId: company?.id as string,
        },
        refetchQueries: combinedRefetchQueries,
        awaitRefetchQueries: true,
    };

    const loading = followLoading || unfollowLoading;
    const follow = async () => {
        await followMutation(mutationParams);
    };
    const unFollow = async () => {
        await unfollowMutation(mutationParams);
    };

    const handleFollowClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        logEventDynamic(AnalyticsEvent.Follow);
        e.stopPropagation();
        await createAuthenticatedHandler(follow);
    };
    const handleUnFollowClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        logEventDynamic(AnalyticsEvent.Unfollow);
        e.stopPropagation();
        await createAuthenticatedHandler(unFollow);
    };
    if (user) {
        return { handleUnFollowClick, handleFollowClick, isFollowing: user.isFollowed, loading };
    }
    if (company) {
        return {
            handleUnFollowClick,
            handleFollowClick,
            isFollowing: company.isFollowingCompany,
            loading,
        };
    }
    return {
        handleUnFollowClick: undefined,
        handleFollowClick: undefined,
        isFollowing: undefined,
        loading: followLoading || unfollowLoading,
    };
};

export default useFollowUnfollow;
